import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Briefcase, Users, Layers, UserPlus } from "lucide-react";
import { toast } from "sonner";
import { api, useAuth, formatApiError } from "@/lib/auth";
import { EndClientDialog } from "@/components/intermediary/EndClientDialog";

const CHOICES = [
  { v: "own", l: "Mes propres projets", d: "Je commande pour moi ou ma structure.", Icon: Briefcase },
  { v: "clients", l: "Les projets de mes clients", d: "Je suis une agence ou un intermédiaire.", Icon: Users },
  { v: "both", l: "Les deux", d: "Mes projets et ceux de mes clients.", Icon: Layers },
];

export function AccountUsagePrompt() {
  const { user, refresh } = useAuth();
  const [step, setStep] = useState(null);
  const [dialog, setDialog] = useState(false);

  const send = async (payload, next) => {
    try { await api.post("/me/account-usage", payload); setStep(next); await refresh(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  if (step === "add") {
    return (
      <div className="cs-card p-6 mt-10 border-[#0E7C90]/40" data-testid="onboarding-add-client">
        <div className="cs-eyebrow text-[#0E7C90]">Vos clients finaux</div>
        <h2 className="cs-h3 mt-2 text-2xl">Ajoutez votre premier client.</h2>
        <p className="mt-2 text-sm text-neutral-600 max-w-2xl">
          Enregistrez vos clients une seule fois : vous pourrez ensuite les choisir sur chaque commande et décider
          si la facture est à votre nom ou au leur. Vos clients n'ont pas de compte et n'accèdent jamais à votre espace.
        </p>
        <div className="mt-5 flex flex-wrap gap-3">
          <button onClick={() => setDialog(true)} className="cs-btn-primary" data-testid="onboarding-add-client-btn"><UserPlus size={15} /> Ajouter un client</button>
          <button onClick={() => setStep("done")} className="cs-btn-ghost !text-black !border-black" data-testid="onboarding-add-later">Plus tard</button>
        </div>
        <EndClientDialog open={dialog} onOpenChange={setDialog} onSaved={() => setStep("done")} />
      </div>
    );
  }
  if (step === "done") {
    return (
      <div className="cs-card p-5 mt-10 text-sm" data-testid="onboarding-done">
        C'est noté. Retrouvez vos clients finaux dans <Link to="/espace/clients" className="cs-link-underline text-[#0E7C90]">Mes clients</Link>. Ce choix reste modifiable dans votre profil.
      </div>
    );
  }
  if (!user || user.role === "admin" || user.account_usage || user.account_usage_dismissed) return null;

  return (
    <div className="cs-card p-6 mt-10" data-testid="account-usage-prompt">
      <div className="cs-eyebrow text-[#0E7C90]">Personnaliser votre espace</div>
      <h2 className="cs-h3 mt-2 text-2xl">Pour qui commandez-vous ?</h2>
      <p className="mt-2 text-sm text-neutral-600">Ce choix adapte seulement votre accueil : toutes les fonctionnalités restent disponibles.</p>
      <div className="mt-5 grid md:grid-cols-3 gap-3">
        {CHOICES.map(({ v, l, d, Icon }) => (
          <button key={v} onClick={() => send({ account_usage: v }, v === "own" ? "done" : "add")}
            className="text-left border border-black/20 bg-white p-4 hover:border-[#0E7C90] hover:bg-[#0E7C90]/5 transition-colors"
            data-testid={`account-usage-${v}`}>
            <Icon size={18} className="text-[#0E7C90]" />
            <div className="mt-2 font-serif text-lg">{l}</div>
            <div className="text-[12px] text-neutral-500">{d}</div>
          </button>
        ))}
      </div>
      <button onClick={() => send({ later: true }, "hidden")} className="mt-4 text-[13px] font-mono text-neutral-500 hover:text-black underline" data-testid="account-usage-later">
        Plus tard
      </button>
    </div>
  );
}
